import React, { useState, useEffect } from "react";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { useQuote } from "../context/QuoteContext";

const NAV = [
    { label: "Home", to: "/" },
    { label: "Catalogo", to: "/catalogo" },
    { label: "Preventivo", to: "/preventivo" },
    { label: "Corsi", to: "/corsi" },
    { label: "Notifiche", to: "/notifiche" },
    { label: "Contatti", to: "/contatti" },
];

export const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { items } = useQuote();
    const badge = items.length > 0 ? items.length : null;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Chiude il menu mobile su cambio route
    useEffect(() => {
        setOpen(false);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 left-0 right-0 z-40 transition-all duration-300 ${
                scrolled
                    ? "bg-white border-b border-zinc-200 shadow-sm"
                    : "bg-white shadow-sm"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex items-center gap-4 group"
                >
                    <div className="h-[4.5rem] w-28 flex items-center justify-center bg-white px-2 py-1">
                        <img
                            src="/nsp-logo.png"
                            alt="NSP Nolo Service Point"
                            className="h-full w-full object-contain"
                        />
                    </div>
                    <div className="hidden sm:block leading-tight">
                        <div className="font-display text-base uppercase tracking-tight text-nsp-green">
                            Nolo Service Point
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                            BigMat Ediltutto
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1 bg-gray-50 rounded-full p-1">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={({ isActive }) =>
                                `px-4 py-2 text-xs xl:text-sm font-semibold tracking-wide transition-all whitespace-nowrap rounded-full ${
                                    isActive
                                        ? "bg-white text-nsp-green shadow-sm"
                                        : "text-gray-500 hover:text-nsp-green"
                                }`
                            }
                        >
                            {item.label}
                            {item.to === "/preventivo" && badge != null && (
                                <span
                                    data-testid="nav-preventivo-badge"
                                    className="ml-1.5 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 bg-nsp-yellow text-nsp-green text-xs font-extrabold rounded-full align-middle shadow-sm ring-1 ring-nsp-yellow-dark/40"
                                >
                                    {badge}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href="tel:3272237610"
                        data-testid="header-phone"
                        className="flex items-center gap-2 text-sm font-semibold whitespace-nowrap text-nsp-green hover:text-nsp-green-dark"
                    >
                        <Phone className="w-4 h-4" strokeWidth={2.5} />
                        327 2237610
                    </a>
                    <Link
                        to="/preventivo"
                        data-testid="header-cta-preventivo"
                        className="relative rounded-full bg-nsp-green text-white px-5 py-2.5 font-bold text-xs hover:bg-nsp-green-dark hover:shadow-lg transition-all active:scale-95 flex items-center gap-2 whitespace-nowrap shadow-md shadow-nsp-green/20"
                    >
                        Preventivo <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                        {badge != null && (
                            <span
                                data-testid="header-cta-badge"
                                className="absolute -top-2.5 -right-2.5 min-w-[22px] h-[22px] px-1.5 bg-nsp-yellow text-nsp-green text-xs font-extrabold rounded-full flex items-center justify-center shadow-md ring-2 ring-white"
                            >
                                {badge}
                            </span>
                        )}
                    </Link>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    className="lg:hidden p-2 text-nsp-green"
                    aria-label="Apri menu"
                >
                    {open ? <X size={28} /> : <Menu size={28} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-nsp-green text-white border-t-2 border-nsp-yellow"
                >
                    <nav className="px-6 py-6 flex flex-col gap-1">
                        {NAV.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                                onClick={() => setOpen(false)}
                                className="py-3 text-sm font-semibold uppercase tracking-wider border-b border-white/10 hover:text-nsp-yellow"
                            >
                                {item.label}
                                {item.to === "/preventivo" && badge != null && (
                                    <span className="ml-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 bg-nsp-yellow text-nsp-green text-xs font-extrabold rounded-full align-middle">
                                        {badge}
                                    </span>
                                )}
                            </Link>
                        ))}
                        <a
                            href="tel:3272237610"
                            className="mt-4 bg-nsp-yellow text-nsp-green px-5 py-4 font-bold uppercase tracking-wider text-xs text-center"
                        >
                            Chiama: 327 2237610
                        </a>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Header;
